import { Autocomplete, InputAdornment, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { Property } from 'types';
import { Tag } from 'types/Tag';
import { LocalOffer } from '@mui/icons-material';
import { FormControl } from './Controls/FormControl';

export type EditFormProps<TItem> = {
    item?: TItem;
    properties?: Property[];
    enableTags?: boolean,
    tagOptions?: string[];
    onChange?: (data: TItem) => void;
};

export function EditForm({ item = {}, properties, enableTags, tagOptions, onChange }: EditFormProps<Record<string, any>>) {
    const [hasLoaded, setHasLoaded] = useState(false);


    // Load the existing data
    useEffect(() => {
        if (item && properties && !hasLoaded) {
            const initialFormData: Record<string, any> = properties.reduce((prev, curr) => {
                return { ...prev, [curr.name]: item[curr.name] || null };
            }, {});
            setHasLoaded(true);
            if (item.tags)
                initialFormData.tags = item.tags;
        }
    }, [item, properties, hasLoaded]);

    return (
        <Stack gap={2}>

            {enableTags && <Autocomplete
                fullWidth
                aria-label="tags"
                id="tags"
                data-testid="tags"
                multiple
                options={tagOptions || []}
                value={(item?.tags || []).map((tag: Tag) => tag.value)}
                onChange={(event, newValue) => {
                    const cleanTags = newValue.map((tag) => {
                        return { value: tag.replace("(create new)", "") };
                    })
                    const newFormData = { ...item, tags: cleanTags };
                    if (onChange)
                        onChange(newFormData);
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                freeSolo
                filterOptions={(options, params) => {
                    const filtered = options.filter(option =>
                        option.toLowerCase().includes(params.inputValue.toLowerCase())
                    );
                    if (
                        params.inputValue &&
                        !options.includes(params.inputValue) &&
                        !(item?.tags || []).some((tag: Tag) => tag.value === params.inputValue)
                    ) {
                        filtered.push(`${params.inputValue} (create new)`);
                    }
                    return filtered;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={"[+] Add tags"}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        <LocalOffer />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />}

            {(properties || []).map((property, index) => {
                return <FormControl
                    property={property}
                    value={item[property.name]}
                    onChange={(value) => onChange({ ...item, [property.name]: value })}
                />
            })}
        </Stack>
    );
}